import moment from 'moment';

const TARGETS_API = 'https://system.sales-management.cloud/api/targets.php';
const SHIFTS_API = 'https://system.sales-management.cloud/api/shifts.php';

const resolveBaseDate = (baseDate) => {
  const candidate = baseDate ? moment(baseDate) : moment();
  return candidate.isValid() ? candidate : moment();
};

const isTargetUser = (rowUserId, userId) => {
  if (!userId) {
    return true;
  }

  return String(rowUserId) === String(userId);
};

const calculateMonthlyWorkStats = (calendarData, userId) => {
  let shiftRegistrationCount = 0;
  let attendanceWorkDays = 0;

  Object.values(calendarData || {}).forEach((dayData) => {
    const shifts = (dayData?.shifts || []).filter((row) => isTargetUser(row?.user_id, userId));
    shiftRegistrationCount += shifts.length;

    const attendanceRows = (dayData?.attendance || []).filter((row) => isTargetUser(row?.user_id, userId));
    const hasPresent = attendanceRows.some((row) => row?.status === 'present');

    if (hasPresent) {
      attendanceWorkDays += 1;
    }
  });

  return {
    shiftRegistrationCount,
    attendanceWorkDays,
  };
};

/**
 * 複数月のデータを取得する
 * @param {string} token - 認証トークン
 * @param {string} apiUrl - API URL
 * @param {string} userId - ユーザーID（オプション）
 * @param {string|Date|moment.Moment|null} baseDate - 基準日（オプション）
 * @returns {Promise} { thisMonth (with targets), lastMonth, twoMonthsAgo }
 */
export const fetchMultiMonthAnalytics = async (token, apiUrl, userId = null, baseDate = null) => {
  const today = moment();
  const referenceDate = resolveBaseDate(baseDate);
  const isCurrentReferenceMonth = referenceDate.isSame(today, 'month');

  const periods = {
    thisMonth: {
      label: '当月',
      from: referenceDate.clone().startOf('month').format('YYYY-MM-DD'),
      to: (isCurrentReferenceMonth ? today.clone() : referenceDate.clone().endOf('month')).format('YYYY-MM-DD'),
    },
    lastMonth: {
      label: '前月',
      from: referenceDate.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      to: referenceDate.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    },
    twoMonthsAgo: {
      label: '前々月',
      from: referenceDate.clone().subtract(2, 'month').startOf('month').format('YYYY-MM-DD'),
      to: referenceDate.clone().subtract(2, 'month').endOf('month').format('YYYY-MM-DD'),
    },
  };

  try {
    const requests = Object.entries(periods).map(([key, period]) => {
      const url = new URL(apiUrl);
      url.searchParams.append('start_date', period.from);
      url.searchParams.append('end_date', period.to);
      url.searchParams.append('limit', '1000');
      url.searchParams.append('include_analytics', 'true');

      if (userId) {
        url.searchParams.append('user_id', userId);
      }

      return fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }).then(async (res) => {
        const result = await res.json();
        return {
          key,
          period,
          data: result.data?.analytics || {},
          status: result.status,
        };
      });
    });

    const shiftRequests = Object.entries(periods).map(([key, period]) => {
      const url = new URL(SHIFTS_API);
      url.searchParams.append('start_date', period.from);
      url.searchParams.append('end_date', period.to);

      if (userId) {
        url.searchParams.append('user_id', String(userId));
      }

      return fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }).then(async (res) => {
        const result = await res.json();
        return {
          key,
          status: result.status,
          data: result.data || {},
        };
      });
    });

    const [results, shiftResults] = await Promise.all([
      Promise.all(requests),
      Promise.all(shiftRequests),
    ]);

    const shiftStatsByKey = shiftResults.reduce((acc, item) => {
      if (item.status === 'success') {
        acc[item.key] = calculateMonthlyWorkStats(item.data, userId);
      } else {
        acc[item.key] = {
          shiftRegistrationCount: 0,
          attendanceWorkDays: 0,
        };
      }

      return acc;
    }, {});

    const output = {};

    let thisMonthTargets = {};
    let thisMonthTargetItems = [];
    try {
      const targetsUrl = new URL(TARGETS_API);
      targetsUrl.searchParams.append('year', String(referenceDate.year()));
      targetsUrl.searchParams.append('month', String(referenceDate.month() + 1));
      if (userId) {
        targetsUrl.searchParams.append('user_id', String(userId));
      }

      const targetRes = await fetch(
        targetsUrl.toString(),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const targetData = await targetRes.json();
      if (targetData.status === 'success') {
        thisMonthTargets = targetData.data?.targets || {};
        thisMonthTargetItems = targetData.data?.target_items || [];
      }
    } catch (err) {
      console.error('Failed to fetch targets:', err);
    }

    results.forEach(({ key, period, data, status }) => {
      if (status === 'success') {
        const workStats = shiftStatsByKey[key] || {
          shiftRegistrationCount: 0,
          attendanceWorkDays: 0,
        };

        output[key] = {
          label: period.label,
          period,
          metrics: data.metrics || [],
          summary: data.summary || {},
          daily_data: data.daily_data || [],
          shift_registration_count: workStats.shiftRegistrationCount,
          attendance_work_days: workStats.attendanceWorkDays,
          targets: key === 'thisMonth' ? thisMonthTargets : {},
          target_items: key === 'thisMonth' ? thisMonthTargetItems : [],
        };
      }
    });

    return output;
  } catch (err) {
    console.error('Failed to fetch multi-month analytics:', err);
    throw err;
  }
};

/**
 * メトリクスをメトリクス値の差分で計算（前月比など）
 * @param {Object} current - 当月データ
 * @param {Object} previous - 前月データ
 * @param {Array} metrics - メトリクス一覧
 * @returns {Object} 差分データ
 */
export const calculateMetricsDifference = (current, previous, metrics) => {
  const diff = {};

  metrics.forEach(metric => {
    const currentVal = parseFloat(current.summary?.[metric] || 0);
    const previousVal = parseFloat(previous.summary?.[metric] || 0);
    const difference = currentVal - previousVal;
    const percentageChange = previousVal !== 0 ? ((difference / previousVal) * 100).toFixed(1) : 0;

    diff[metric] = {
      current: currentVal,
      previous: previousVal,
      difference,
      percentageChange,
      isPositive: difference >= 0,
    };
  });

  return diff;
};
